import useJobStream from '../hooks/useJobStream';
import LiveJobStatus from '../components/LiveJobStatus';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Modal,
  Spinner,
  Table,
} from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import bakingApi from '../services/bakingApi';
import {
  getClientRuntimeAvailability,
  isAiRiggingAutoskinEnabled,
  isAiRiggingExportEnabled,
  isAiRiggingJobCreationEnabled,
  isAiRiggingUiEnabled,
  operationLabel,
} from '../features/aiRigging';
import { shortJobId } from '../features/riggingJobStatus';
import RiggingJobStatusBadge from '../components/rigging/RiggingJobStatusBadge';
import RiggingRuntimeNotice from '../components/rigging/RiggingRuntimeNotice';
import { hasTenantPermission, RIGGING_JOB_CREATE, RIGGING_JOB_VIEW } from '../auth/permissions';
import { useTenantPermissions } from '../hooks/useTenantPermissions';
import { useAuth } from '../context/AuthContext';

function EmptyState({ title, copy, actionLabel, onAction, actionTo }) {
  return (
    <div className="product-card empty-state" data-testid="partner-rigging-jobs-empty">
      <h2 className="h5">{title}</h2>
      <p className="text-muted mb-3">{copy}</p>
      {actionTo ? (
        <Button as={Link} to={actionTo} variant="outline-primary">
          {actionLabel}
        </Button>
      ) : null}
      {onAction ? (
        <Button variant="outline-primary" onClick={onAction}>
          {actionLabel}
        </Button>
      ) : null}
    </div>
  );
}

function StatusSummary({ statusCounts = {} }) {
  const total = Object.values(statusCounts).reduce((sum, n) => sum + (n || 0), 0);
  const cells = [
    ['Total', total],
    ['Queued', statusCounts.queued || 0],
    ['Running', statusCounts.running || 0],
    ['Succeeded', statusCounts.succeeded || 0],
    ['Failed', statusCounts.failed || 0],
    ['Cancelled', statusCounts.canceled || 0],
  ];
  return (
    <div className="row g-2 mb-3" data-testid="partner-rigging-jobs-status-summary">
      {cells.map(([label, value]) => (
        <div className="col-6 col-md-2" key={label}>
          <div className="product-card py-2 px-3 h-100">
            <div className="text-muted small">{label}</div>
            <div className="fs-5 fw-semibold mb-0">{value}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function isMayaProject(project) {
  const provider = String(
    project?.execution_provider || project?.resolved_execution_provider || ''
  ).toLowerCase();
  return provider === 'maya';
}

export default function PartnerRiggingJobsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { permissions, hasTenantPermission: can } = useTenantPermissions();
  const { aiRiggingFlags, capabilitiesLoaded } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorKind, setErrorKind] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const closeButtonRef = useRef(null);

  const statusFilter = searchParams.get('status') || '';
  const operationFilter = searchParams.get('operation') || '';
  const projectFilter = searchParams.get('project') || '';
  const search = searchParams.get('search') || '';
  const ordering = searchParams.get('ordering') || '-created_at';

  const aiEnabled =
    capabilitiesLoaded && aiRiggingFlags != null
      ? Boolean(aiRiggingFlags.ai_rigging_enabled)
      : isAiRiggingUiEnabled();

  const canView =
    can(RIGGING_JOB_VIEW) || hasTenantPermission(permissions, RIGGING_JOB_VIEW);
  const canCreate =
    (can(RIGGING_JOB_CREATE) || hasTenantPermission(permissions, RIGGING_JOB_CREATE)) &&
    isAiRiggingJobCreationEnabled();
  const { revision: liveRevision, status: liveStatus } = useJobStream({ rigging: true, enabled: aiEnabled && canView });

  const runtime = availability || getClientRuntimeAvailability();

  const eligibleMayaProjects = useMemo(
    () => (projects || []).filter(isMayaProject),
    [projects]
  );

  useEffect(() => {
    if (!aiEnabled || !canView) return undefined;
    let cancelled = false;
    (async () => {
      if (liveRevision === 0) setLoading(true);
      setError(null);
      setErrorKind(null);
      try {
        const [jobPayload, projectList] = await Promise.all([
          bakingApi.listPartnerRiggingJobs({
            status: statusFilter,
            operation: operationFilter,
            project: projectFilter,
            search,
            ordering,
          }),
          bakingApi.listProjects().catch(() => []),
        ]);
        if (cancelled) return;
        if (!jobPayload || !Array.isArray(jobPayload.results)) {
          setErrorKind('malformed');
          setError('Rigging Jobs could not be loaded. Refresh the page or try again later.');
          setJobs([]);
          return;
        }
        setJobs(jobPayload.results);
        setStatusCounts(jobPayload.status_counts || {});
        setAvailability(jobPayload.runtime_availability || null);
        setProjects(Array.isArray(projectList) ? projectList : projectList?.results || []);
      } catch (err) {
        if (cancelled) return;
        if (err.status === 403) {
          setErrorKind('forbidden');
          setError(
            err.message ||
              'You do not have permission to view Rigging Jobs, or AI Rigging is not enabled.'
          );
        } else {
          setErrorKind('api');
          setError('Rigging Jobs could not be loaded. Refresh the page or try again later.');
        }
        setJobs([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    aiEnabled,
    canView,
    statusFilter,
    operationFilter,
    projectFilter,
    search,
    ordering,
    liveRevision,
  ]);

  useEffect(() => {
    if (pickerOpen && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [pickerOpen]);

  const updateFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value) next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  const openNewJobFlow = () => {
    if (!canCreate || loading) return;
    if (eligibleMayaProjects.length === 1) {
      navigate(`/console/projects/${eligibleMayaProjects[0].id}/rigging/jobs/new`);
      return;
    }
    setPickerOpen(true);
  };

  const chooseProject = (projectId) => {
    setPickerOpen(false);
    navigate(`/console/projects/${projectId}/rigging/jobs/new`);
  };

  const filtersActive = Boolean(statusFilter || operationFilter || projectFilter || search);

  const operationOptions = useMemo(() => {
    const options = [
      { value: 'maya_project_setup', label: 'Project Setup' },
      { value: 'maya_skeleton_build', label: 'Build Skeleton' },
      { value: 'maya_rig_build', label: 'Build Rig' },
    ];
    if (isAiRiggingAutoskinEnabled()) {
      options.push({ value: 'maya_ai_autoskin', label: 'AI AutoSkin' });
    }
    if (isAiRiggingExportEnabled()) {
      options.push({ value: 'maya_engine_export', label: 'Engine Export' });
    }
    if (isAiRiggingAutoskinEnabled() && isAiRiggingExportEnabled()) {
      options.push({
        value: 'maya_character_pipeline',
        label: 'Complete Character Pipeline',
      });
    }
    return options;
  }, []);

  if (!aiEnabled) {
    return (
      <div className="error-state" data-testid="partner-rigging-jobs-feature-disabled">
        <h1 className="h4">AI Rigging is not enabled</h1>
        <p>Rigging Jobs are not available in this environment.</p>
      </div>
    );
  }

  if (!canView && permissions.length) {
    return (
      <div className="error-state" data-testid="partner-rigging-jobs-forbidden">
        You do not have permission to view Rigging Jobs.
      </div>
    );
  }

  let empty = null;
  if (!loading && !error && jobs.length === 0) {
    if (filtersActive) {
      empty = (
        <EmptyState
          title="No matching Rigging Jobs"
          copy="No Rigging Jobs match the current filters. Clear filters or try a different search."
        />
      );
    } else {
      empty = (
        <EmptyState
          title="No rigging jobs yet."
          copy="Create your first Rigging Job from an eligible Maya project."
          actionLabel={canCreate ? 'Create your first Rigging Job' : null}
          onAction={canCreate ? openNewJobFlow : null}
        />
      );
    }
  }

  return (
    <div data-testid="partner-rigging-jobs-page">
      <LiveJobStatus status={liveStatus} />
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h1 className="product-page-title">Rigging Jobs</h1>
          <p className="product-page-subtitle mb-0">
            Create and monitor Maya character-rigging operations across your Partner projects.
          </p>
        </div>
        {canCreate ? (
          <Button
            onClick={openNewJobFlow}
            disabled={loading}
            data-testid="partner-new-rigging-job-action"
          >
            New Rigging Job
          </Button>
        ) : (
          <Button
            disabled
            data-testid="partner-new-rigging-job-action"
            title="Job creation requires permission and AI_RIGGING_JOB_CREATION_ENABLED"
          >
            New Rigging Job
          </Button>
        )}
      </div>

      <RiggingRuntimeNotice availability={runtime} className="mb-3" />

      {!canCreate && isAiRiggingJobCreationEnabled() === false ? (
        <Alert variant="info" className="mb-3" data-testid="partner-rigging-creation-disabled">
          New Rigging Job is unavailable because job creation is disabled. Existing jobs remain
          visible for review.
        </Alert>
      ) : null}

      {!loading && !error ? <StatusSummary statusCounts={statusCounts} /> : null}

      <Form className="row g-2 mb-3" data-testid="partner-rigging-jobs-filters">
        <div className="col-md-3">
          <Form.Label visuallyHidden htmlFor="partner-rigging-search">
            Search
          </Form.Label>
          <Form.Control
            id="partner-rigging-search"
            placeholder="Search jobs"
            value={search}
            onChange={(e) => updateFilter('search', e.target.value)}
            aria-label="Search jobs"
          />
        </div>
        <div className="col-md-3">
          <Form.Label visuallyHidden htmlFor="partner-rigging-project">
            Project
          </Form.Label>
          <Form.Select
            id="partner-rigging-project"
            aria-label="Project filter"
            value={projectFilter}
            onChange={(e) => updateFilter('project', e.target.value)}
          >
            <option value="">All projects</option>
            {eligibleMayaProjects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Form.Select>
        </div>
        <div className="col-md-2">
          <Form.Label visuallyHidden htmlFor="partner-rigging-status">
            Status
          </Form.Label>
          <Form.Select
            id="partner-rigging-status"
            aria-label="Status filter"
            value={statusFilter}
            onChange={(e) => updateFilter('status', e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="queued">Queued</option>
            <option value="running">Running</option>
            <option value="succeeded">Succeeded</option>
            <option value="failed">Failed</option>
            <option value="canceled">Cancelled</option>
            <option value="cancel_requested">Cancel requested</option>
          </Form.Select>
        </div>
        <div className="col-md-2">
          <Form.Label visuallyHidden htmlFor="partner-rigging-operation">
            Operation
          </Form.Label>
          <Form.Select
            id="partner-rigging-operation"
            aria-label="Operation filter"
            value={operationFilter}
            onChange={(e) => updateFilter('operation', e.target.value)}
          >
            <option value="">All operations</option>
            {operationOptions.map((op) => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </Form.Select>
        </div>
        <div className="col-md-2">
          <Form.Label visuallyHidden htmlFor="partner-rigging-ordering">
            Ordering
          </Form.Label>
          <Form.Select
            id="partner-rigging-ordering"
            aria-label="Ordering"
            value={ordering}
            onChange={(e) => updateFilter('ordering', e.target.value)}
          >
            <option value="-created_at">Newest first</option>
            <option value="created_at">Oldest first</option>
            <option value="status">Status A–Z</option>
            <option value="-status">Status Z–A</option>
          </Form.Select>
        </div>
      </Form>

      {loading ? (
        <div className="loading-state" role="status" aria-live="polite">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading rigging jobs…
        </div>
      ) : null}
      {error ? (
        <div
          className="error-state"
          role="alert"
          data-testid={`partner-rigging-jobs-error-${errorKind || 'api'}`}
        >
          {error}
        </div>
      ) : null}
      {empty}
      {!loading && !error && jobs.length > 0 ? (
        <div className="product-card p-0">
          <Table responsive hover className="product-table mb-0">
            <thead>
              <tr>
                <th scope="col">Job</th>
                <th scope="col">Project</th>
                <th scope="col">Character</th>
                <th scope="col">Operation</th>
                <th scope="col">Status</th>
                <th scope="col" className="d-none d-md-table-cell">
                  Created
                </th>
                <th scope="col" className="d-none d-lg-table-cell">
                  Created By
                </th>
                <th scope="col" className="d-none d-lg-table-cell">
                  Runtime
                </th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => {
                const projectId = job.project?.id;
                const detailTo = projectId
                  ? `/console/projects/${projectId}/rigging/jobs/${job.id}`
                  : null;
                const projectTo = projectId ? `/console/projects/${projectId}` : null;
                return (
                  <tr key={job.id}>
                    <td>
                      {detailTo ? (
                        <Link to={detailTo}>{shortJobId(job.id)}</Link>
                      ) : (
                        shortJobId(job.id)
                      )}
                    </td>
                    <td>
                      {projectTo ? (
                        <Link to={projectTo}>{job.project?.name || 'Project'}</Link>
                      ) : (
                        job.project?.name || '—'
                      )}
                    </td>
                    <td>{job.character?.name || job.character_display_name || '—'}</td>
                    <td>{operationLabel(job.operation)}</td>
                    <td>
                      <RiggingJobStatusBadge
                        job={job}
                        workerRuntimeEnabled={Boolean(
                          runtime.worker_runtime_enabled ?? runtime.workerRuntimeEnabled
                        )}
                      />
                    </td>
                    <td className="small d-none d-md-table-cell">
                      {job.created_at ? new Date(job.created_at).toLocaleString() : '—'}
                    </td>
                    <td className="d-none d-lg-table-cell">
                      {job.created_by?.display_name || job.created_by_username || '—'}
                    </td>
                    <td className="small d-none d-lg-table-cell">
                      {job.runtime_available
                        ? job.latest_attempt
                          ? `Attempt ${job.latest_attempt.attempt_number}: ${job.latest_attempt.status}`
                          : 'Runtime available'
                        : 'Runtime unavailable'}
                    </td>
                    <td>
                      {detailTo ? (
                        <Button as={Link} to={detailTo} size="sm" variant="outline-primary">
                          View
                        </Button>
                      ) : (
                        <span className="text-muted small">Project unavailable</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      ) : null}

      <Modal
        show={pickerOpen}
        onHide={() => setPickerOpen(false)}
        aria-labelledby="choose-maya-project-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="choose-maya-project-title">Choose Maya Project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {eligibleMayaProjects.length === 0 ? (
            <div data-testid="partner-rigging-no-eligible-projects">
              <h2 className="h5">No eligible Maya projects</h2>
              <p className="text-muted">
                Create or configure a Maya project before creating a Rigging Job.
              </p>
              <Button as={Link} to="/console/projects" variant="outline-primary">
                Go to Projects
              </Button>
            </div>
          ) : (
            <ul className="list-unstyled mb-0">
              {eligibleMayaProjects.map((project) => (
                <li key={project.id} className="mb-2">
                  <Button
                    variant="outline-secondary"
                    className="w-100 text-start"
                    onClick={() => chooseProject(project.id)}
                  >
                    {project.name}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            ref={closeButtonRef}
            variant="secondary"
            onClick={() => setPickerOpen(false)}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
